import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import dayjs from 'dayjs'
import { TEAM_NUMERABLE_TYPE } from 'shared/constants/strange'
import { DayjsFormats, getTimezonesList, logger } from 'shared/lib'
import { Box, type IDropdownItem, toastStore, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import {
  type IResponseBusinessHours,
  type IResponseBusinessHoursDay,
  type IResponseBusinessHoursDayKey,
} from 'entities/Users/api/types'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { InboxesApi } from 'entities/Inbox'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import {
  EmojiAction,
  makeSavedReplyInputAction,
  mergeFieldReplacer,
  MessageFieldStore,
  SavedRepliesAction,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import { type InboxSettingsStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/store/inboxSettingsStore'

export class BusinessHoursStore {
  private _businessHours: IResponseBusinessHours | null = null
  private _timezone: IDropdownItem | null = null
  private _days: IResponseBusinessHoursDay[] = [
    {
      day: 'sun',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'mon',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'tue',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'wed',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'thu',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'fri',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
    {
      day: 'sat',
      start: '09:00:00',
      end: '21:00:00',
      closed: false,
    },
  ]
  private _disposeBlurReaction: IReactionDisposer | null = null
  private _disposeAttachmentReaction: IReactionDisposer | null = null
  private _isActive = false
  private _modalId = nanoid()
  private _messageFieldStore: MessageFieldStore
  private _savedRepliesStore: SavedRepliesStore
  private _attachments: Attachment[] = []
  private _message =
    "Thanks for your message! We're currently outside business hours. We'll get back to you during our next business day."
  private _messageFieldError = ''
  private _selectedScheduleType = 'every_day'

  constructor(private _inboxSettingsStore: InboxSettingsStore, private _entities: IEntitiesStore) {
    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this._messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write a message...',
        styles: {
          minHeight: 106,
          width: 378,
        },
        showActionsItems: 5,
        resetOnUnmount: false,
        textLimit: {
          maxLength: 1600,
          message: 'Message should not be longer than 1.600 characters',
        },
        makeInputActions: (store) => [makeSavedReplyInputAction(store, this._savedRepliesStore)],
        makeActions: () => [
          { iconButtonComponent: <MediaAction /> },
          { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this.onAddMergeField(value)}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
        makeEvents: () => ({
          onFocus: () => {
            this.removeError()
          },
        }),
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  init = (inbox: IResponseInbox) => {
    this._businessHours = inbox.business_hours ?? null
    this._isActive = inbox.business_hours?.is_active ?? false
    const timezoneValue = inbox.business_hours?.timezone ?? dayjs.tz.guess()
    this._timezone = this.timezonesList.find((tz) => tz.value === timezoneValue) ?? null
    this._attachments =
      inbox.business_hours?.media_url?.map(
        (item) => new Attachment({ responseMediaShort: item })
      ) ?? []
    this._message = inbox.business_hours?.message ?? this._message

    this._messageFieldStore.setMessageData({
      message: this._message,
      attachments: this._attachments,
      isReset: false,
      isReplace: true,
      isFocus: false,
    })

    this._days = this._businessHours?.days ?? this._days
    this._selectedScheduleType = this.deriveScheduleType()

    this.reactionFieldBlur()
    this.reactionAttachmentChange()
  }

  reactionFieldBlur = () => {
    this._disposeBlurReaction?.()
    this._disposeBlurReaction = reaction(
      () => this._messageFieldStore.activeMessageField,
      (isActive) => {
        if (!isActive) {
          const currentText = this._messageFieldStore.text
          const currentAttachments = this._messageFieldStore.attachments

          const prevAttachments = this._attachments
          const attachmentsAreSame =
            prevAttachments.length === currentAttachments.length &&
            prevAttachments.every((attachment) => currentAttachments.includes(attachment))

          const prevText = this._message
          const textIsSame = prevText === currentText

          if (!attachmentsAreSame || !textIsSame) {
            this.updateBusinessHours()
          }
        }
      }
    )
  }

  reactionAttachmentChange = () => {
    this._disposeAttachmentReaction?.()
    let previousAttachmentsIds = new Set(
      this._messageFieldStore.attachments.map((attachment) => attachment.id)
    )

    this._disposeAttachmentReaction = reaction(
      () => this._messageFieldStore.attachments.map((attachment) => attachment.id),
      (currentAttachmentsIds) => {
        const currentAttachmentsIdsSet = new Set(currentAttachmentsIds)
        const hasNewAttachment = currentAttachmentsIdsSet.size !== previousAttachmentsIds.size

        if (hasNewAttachment) {
          this._messageFieldStore.setActiveMessageField(true)
          this._messageFieldStore.setFocusMessageFieldTrigger()
        }

        previousAttachmentsIds = currentAttachmentsIdsSet
      }
    )
  }

  get businessHours() {
    return this._businessHours
  }

  get timezonesList() {
    return getTimezonesList().map((tz) => ({
      id: tz.value,
      label: tz.label,
      value: tz.value,
      group: tz.group,
      labelContent: (
        <Box display={'flex'}>
          <Typography
            ellipsis
            variant={'text-input-field-r'}
            tooltipProps={{
              label: tz.label,
            }}
            ariaLabel={'timezoneLabel'}
          >
            {tz.label}
          </Typography>
        </Box>
      ),
    }))
  }

  get timezone() {
    return this._timezone
  }

  get isActive() {
    return this._isActive
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get messageFieldError() {
    return this._messageFieldError
  }

  get scheduleType() {
    return [
      {
        id: 'every_day',
        label: 'Every day',
      },
      {
        id: 'custom',
        label: 'Custom',
      },
    ]
  }

  get selectedScheduleType() {
    return this._selectedScheduleType
  }

  get days() {
    return this._days
  }

  get minTime() {
    return this.getCommonDayTime('00:00:00')
  }

  get maxTime() {
    return this.getCommonDayTime('23:59:59')
  }

  getDayLabel = (dayKey: IResponseBusinessHoursDayKey) => {
    const dayLabels: Record<IResponseBusinessHoursDayKey, string> = {
      sun: 'Sunday',
      mon: 'Monday',
      tue: 'Tuesday',
      wed: 'Wednesday',
      thu: 'Thursday',
      fri: 'Friday',
      sat: 'Saturday',
    }
    return dayLabels[dayKey]
  }

  deriveScheduleType = (): 'every_day' | 'custom' => {
    const [firstDay] = this._days
    if (!firstDay) return 'every_day'
    const allOpen = this._days.every((day) => !day.closed)
    const allSameTimes = this._days.every(
      (day) => day.start === firstDay.start && day.end === firstDay.end
    )
    return allOpen && allSameTimes ? 'every_day' : 'custom'
  }

  getCommonDayTime = (timeString: string) => {
    const today = uiStore.dayjsWithoutTimezone().format(DayjsFormats.dateSort)
    return dayjs(`${today} ${timeString}`, DayjsFormats.apiFormat2).toDate()
  }

  getEveryDayTime = (field: 'start' | 'end') => {
    const timeString = this._days[0][field] || '09:00:00'
    return this.getCommonDayTime(timeString)
  }

  setSelectedScheduleType = (scheduleType: string) => {
    this._selectedScheduleType = scheduleType
    this._days.forEach((day) => {
      day.start = '09:00:00'
      day.end = '21:00:00'
      day.closed = (day.day === 'sat' || day.day === 'sun') && scheduleType !== 'every_day'
    })

    this.updateBusinessHours()
  }

  setEveryDayTime = (date: Date | null, field: 'start' | 'end') => {
    if (!date) return

    const timeString = dayjs(date).format('HH:mm:ss')
    this._days.forEach((day) => {
      day[field] = timeString
    })
    this.updateBusinessHours()
  }

  getDayTime = (dayKey: IResponseBusinessHoursDayKey, field: 'start' | 'end') => {
    const day = this._days.find((d) => d.day === dayKey)

    const timeString = day?.[field] ?? '09:00:00'
    return this.getCommonDayTime(timeString)
  }

  setDayTime = (
    dayKey: IResponseBusinessHoursDayKey,
    date: Date | null,
    field: 'start' | 'end'
  ) => {
    const day = this._days.find((d) => d.day === dayKey)
    if (!day || !date) return

    day[field] = dayjs(date).format('HH:mm:ss')
    this.updateBusinessHours()
  }

  toggleDayClosed = (dayKey: IResponseBusinessHoursDayKey) => {
    const day = this._days.find((d) => d.day === dayKey)
    if (!day) return

    day.closed = !day.closed
    this.updateBusinessHours()
  }

  onClose = () => {
    modalStore.closeModal(this._modalId)
  }

  onHandleToggleBusinessHours = () => {
    if (!this.isActive) {
      this._entities.eventLogStore.logEvent(
        'Business_Hours_Enabled',
        {
          event_id: 'business_hours_enabled',
          userId: this._entities.usersStore.user_owner_id,
          agencyId: this._entities.organizationStore.agency_id,
          inboxId: this._inboxSettingsStore.inboxId,
        },
        { groupId: this._entities.organizationStore.id }
      )
    }

    this._isActive = !this._isActive
    this.updateBusinessHours(true)
  }

  toggleIsActive = () => {
    if (this._isActive) {
      return modalStore.addModal({
        id: this._modalId,
        type: ModalTypeList.INFO,
        title: 'Disable business hours?',
        desc: 'Disabling business hours will also disable the smart call flow',
        primaryAction: {
          text: 'Confirm',
          onAction: () => {
            this.onHandleToggleBusinessHours()
            this.onClose()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            this.onClose()
          },
        },
      })
    }

    this.onHandleToggleBusinessHours()
  }

  setTimezone = (timezone: IDropdownItem) => {
    this._timezone = timezone
    this.updateBusinessHours()
  }

  onAddMergeField = (mergeField: MergeField) => {
    if (this._messageFieldStore.addContent) {
      this._messageFieldStore.addContent(
        mergeFieldReplacer(this._entities, mergeField.template) + '&nbsp;'
      )
    }
  }

  removeError = () => {
    this._messageFieldStore.clearHighlight()
    this._messageFieldError = ''
  }

  updateBusinessHours = async (isToggleChange?: boolean) => {
    if (!this._inboxSettingsStore.inboxId) {
      return
    }
    if (this._messageFieldStore.text.length === 0) {
      this._messageFieldStore.setHighlight('error')
      this._messageFieldError = 'Enter your out of office message.'
      return
    }

    try {
      const mediaUrl = this._messageFieldStore.media_url.map(
        (attachment) => attachment.attachmentDraft
      )

      const response = await InboxesApi.updateInboxBusinessHours({
        is_active: this._isActive,
        hourable_type: TEAM_NUMERABLE_TYPE,
        hourable_id: this._inboxSettingsStore.inboxId,
        timezone: this._timezone?.value ?? '',
        days: this._days,
        message: this._messageFieldStore.text,
        media_url: mediaUrl ?? [],
      })
      localStorage.setItem('updateInboxBusinessHours', JSON.stringify(response.data))
      runInAction(() => {
        this._businessHours = response.data
        this._message = this._messageFieldStore.text
        this._attachments = this._messageFieldStore.attachments
      })
      if (isToggleChange) {
        toastStore.add({
          type: 'success',
          title: this._isActive ? 'Business hours activated' : 'Business hours disabled',
        })
      } else {
        toastStore.add({
          type: 'success',
          title: 'Business hours updated',
        })
      }
    } catch (error) {
      if (isToggleChange) {
        this._isActive = !this._isActive
      }
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Failed to update business hours',
      })
    }
  }

  dispose = () => {
    this._disposeBlurReaction?.()
    this._disposeAttachmentReaction?.()
    this._messageFieldStore.reset()
  }
}
